import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { FaFacebook, FaInstagram, FaLinkedin } from "react-icons/fa";
import { Broadcast, PaperPlaneTilt, CalendarBlank, Gear, LinkSimple, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// This is a browser-preview of what the actual browser-extension popup looks like.
// The real extension (in /app/extension) uses the same design in vanilla JS.
export default function ExtensionPopup() {
  const [url, setUrl] = useState(window.location.origin + "/dashboard");
  const [caption, setCaption] = useState("Just shipped this — take a look 👀");
  const [selected, setSelected] = useState(["facebook", "linkedin"]);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    const d = new Date(Date.now() + 20 * 60 * 1000); d.setSeconds(0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    setScheduledAt(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
  }, []);

  const platforms = [
    { id: "facebook", Icon: FaFacebook, color: "#1877F2" },
    { id: "instagram", Icon: FaInstagram, color: "#E4405F" },
    { id: "linkedin", Icon: FaLinkedin, color: "#0A66C2" },
  ];
  const toggle = (id) => setSelected((p) => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const submit = async (immediate) => {
    if (!me) { toast.error("Sign in via the web app first"); return; }
    if (!url) { toast.error("URL required"); return; }
    if (selected.length === 0) { toast.error("Pick a platform"); return; }
    setBusy(true);
    try {
      await api.post("/posts", {
        url, caption, platforms: selected,
        scheduled_at: !immediate && scheduleOn ? new Date(scheduledAt).toISOString() : null,
        source: "extension",
      });
      toast.success(immediate ? "Posting…" : "Scheduled ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center p-8">
      <Toaster theme="dark" position="top-right" richColors />
      <div className="relative">
        <div className="absolute -inset-4 bg-[#FF4500]/25 blur-2xl" />
        <div
          className="relative bg-[#18181B] border border-[#3F3F46] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] flex flex-col"
          style={{ width: 380, height: 520, borderRadius: 12 }}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-[#FF4500] flex items-center justify-center" style={{ borderRadius: 2 }}>
                <Broadcast size={16} weight="fill" color="#fff" />
              </div>
              <span className="font-display text-lg leading-none">clydeflow</span>
            </div>
            <Gear size={16} className="text-[#71717A]" />
          </div>

          <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
            <div>
              <div className="overline mb-1">current tab</div>
              <div className="border border-[#27272A] bg-black/40 p-2.5" style={{ borderRadius: 2 }}>
                <div className="flex items-center gap-1.5 text-[10px] text-[#71717A] font-mono truncate">
                  <LinkSimple size={10} /><span className="truncate">{url}</span>
                </div>
              </div>
            </div>

            <div>
              <div className="overline mb-1">caption</div>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={3}
                className="input text-sm"
                style={{ minHeight: 64 }}
              />
            </div>

            <div>
              <div className="overline mb-1">platforms</div>
              <div className="grid grid-cols-3 gap-1.5">
                {platforms.map(({ id, Icon, color }) => {
                  const on = selected.includes(id);
                  return (
                    <button
                      key={id}
                      onClick={() => toggle(id)}
                      className="flex items-center justify-center py-2 text-xs transition-colors duration-150"
                      style={{
                        border: `1px solid ${on ? color : "#27272A"}`,
                        color: on ? color : "#71717A",
                        background: on ? `${color}18` : "transparent",
                        borderRadius: 2,
                      }}
                    >
                      <Icon size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-[#A1A1AA]">
              <input type="checkbox" checked={scheduleOn} onChange={(e) => setScheduleOn(e.target.checked)} className="accent-[#FF4500]" />
              <CalendarBlank size={12} /> schedule for later
            </label>
            {scheduleOn && (
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="input text-xs"
              />
            )}

            {!me && (
              <div className="text-[10px] text-[#fca5a5] flex items-center gap-1">
                <Sparkle size={10} /> not signed in — open dashboard to authenticate.
              </div>
            )}
          </div>

          <div className="p-3 border-t border-[#27272A] grid grid-cols-2 gap-2">
            <button disabled={busy} onClick={() => submit(true)} className="btn btn-primary text-sm">
              <PaperPlaneTilt size={14} weight="fill" /> post now
            </button>
            <button disabled={busy} onClick={() => submit(false)} className="btn btn-secondary text-sm">
              <CalendarBlank size={14} weight="fill" /> schedule
            </button>
          </div>
        </div>
        <div className="mt-4 text-center text-[10px] text-[#71717A] font-mono uppercase tracking-widest">
          extension popup · 380 × 520
        </div>
      </div>
    </div>
  );
}
